import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Thermometer, PlusCircle, ScanLine } from "lucide-react";
import HelpButton from "@/components/HelpButton";

const LOG_TYPES = [
  { v: "routine", l: "Routine" },
  { v: "cooking", l: "Cooking" },
  { v: "cooling", l: "Cooling" },
  { v: "reheating", l: "Reheating" },
  { v: "hot_holding", l: "Hot Holding" },
  { v: "cold_display", l: "Cold Display" },
];

export default function TemperatureLogs() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";
  const [searchParams, setSearchParams] = useSearchParams();
  const [equipment, setEquipment] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({
    equipment_id: "",
    temperature_c: "",
    log_type: "routine",
    notes: "",
    corrective_action: "",
  });
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScan = async (code) => {
    setScannerOpen(false);
    if (!activeSite || !code) return;
    // 1) Match the equipment we already have in memory (works offline).
    const local = equipment.find(
      (e) => e.barcode_id === code || e.id === code,
    );
    if (local) {
      setForm((f) => ({ ...f, equipment_id: local.id }));
      toast.success(`Scanned: ${local.name}`);
      return;
    }
    // 2) Fallback to the backend (catches equipment added since last load).
    try {
      const { data } = await api.get("/equipment/by-barcode", {
        params: { code, site_id: activeSite.id },
      });
      if (!data) return; // 401 interceptor already handled
      setEquipment((prev) =>
        prev.some((e) => e.id === data.id) ? prev : [data, ...prev],
      );
      setForm((f) => ({ ...f, equipment_id: data.id }));
      toast.success(`Scanned: ${data.name}`);
    } catch (err) {
      toast.error(
        formatErr(err.response?.data?.detail) || `No equipment matches "${code}"`,
      );
    }
  };

  const load = async () => {
    if (!activeSite) return;
    const [eq, lg] = await Promise.all([
      api.get("/equipment", withSite(activeSite.id)),
      api.get("/temperature-logs", withSite(activeSite.id)),
    ]);
    setEquipment(eq.data);
    setLogs(lg.data);
    // Prefer the equipment passed in via ?equipment=<id> (deep-link from
    // the PWA Scan shortcut / QuickScan action menu). Otherwise fall
    // back to the first item so the form is never blank.
    const requested = searchParams.get("equipment");
    if (requested && eq.data.some((e) => e.id === requested)) {
      setForm((f) => ({ ...f, equipment_id: requested }));
    } else if (eq.data.length) {
      // Functional setState reads the LATEST form state so this
      // doesn't overwrite a user's manual pick between load() calls.
      // The old `!form.equipment_id` guard read a stale closure value
      // which was always empty on mount, so the first-eq default
      // *never* fired if load ran twice (activeSite null → non-null).
      setForm((f) => (f.equipment_id ? f : { ...f, equipment_id: eq.data[0].id }));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id || form.temperature_c === "") return;
    setBusy(true);
    try {
      await api.post(
        "/temperature-logs",
        { ...form, temperature_c: parseFloat(form.temperature_c) },
        withSite(activeSite.id)
      );
      toast.success("Temperature log recorded");
      setForm({ ...form, temperature_c: "", notes: "", corrective_action: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="temperature-page">
      <PageHeader
        title="Temperature Monitoring"
        subtitle="Record fridge, freezer, cool room, cooking, cooling, reheating and hot-holding temperatures. Out-of-range readings auto-generate a corrective action."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="temperature" />
            <PdfButton kind="temperature" />
            <HelpButton topic="temperature" />
          </div>
        }
      />

      {canEdit && (
        <form onSubmit={submit} className="surface p-6 md:p-8 mb-8 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-3 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-eucalyptus-700" />
              <h2 className="font-display font-bold text-lg">New temperature reading</h2>
            </div>
            <Button
              type="button"
              data-testid="temp-scan-equipment"
              onClick={() => setScannerOpen(true)}
              className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white gap-2"
            >
              <ScanLine className="w-4 h-4" />
              Scan equipment
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Equipment</Label>
            <Select
              key={`temp-eq-${equipment.length}`}
              value={form.equipment_id}
              onValueChange={(v) => setForm({ ...form, equipment_id: v })}
            >
              <SelectTrigger data-testid="temp-equipment" className="h-12">
                {/*
                  `key` on the parent Select remounts the whole tree
                  once equipment finishes loading, so Radix's internal
                  SelectValue cache re-derives the label. We DON'T mix
                  children + placeholder inside SelectValue — Radix v1
                  crashes with a NotFoundError removeChild cascade in
                  that config (learned the hard way in iter31 → 32).
                */}
                <SelectValue placeholder="Choose equipment" />
              </SelectTrigger>
              <SelectContent>
                {equipment.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} ({e.target_min_c}–{e.target_max_c}°C)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Temperature (°C)</Label>
            <Input
              data-testid="temp-value"
              type="number"
              step="0.1"
              value={form.temperature_c}
              onChange={(e) => setForm({ ...form, temperature_c: e.target.value })}
              className="h-12 font-mono text-lg"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Log type</Label>
            <Select
              value={form.log_type}
              onValueChange={(v) => setForm({ ...form, log_type: v })}
            >
              <SelectTrigger data-testid="temp-type" className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOG_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>
                    {t.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Notes</Label>
            <Input
              data-testid="temp-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label>Corrective action (if breach)</Label>
            <Input
              data-testid="temp-corrective"
              value={form.corrective_action}
              onChange={(e) => setForm({ ...form, corrective_action: e.target.value })}
              className="h-12"
            />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button
              type="submit"
              disabled={busy}
              data-testid="temp-submit"
              className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
            >
              <PlusCircle className="w-4 h-4" />
              Record reading
            </Button>
          </div>
        </form>
      )}

      <div className="surface overflow-hidden">
        {(() => {
          const filterEqId = searchParams.get("filter") === "eq" ? searchParams.get("equipment") : null;
          const visibleLogs = filterEqId
            ? logs.filter((l) => l.equipment_id === filterEqId)
            : logs;
          const filterEqName = filterEqId
            ? (equipment.find((e) => e.id === filterEqId)?.name || "this equipment")
            : null;
          return (
            <>
              {filterEqName && (
                <div
                  data-testid="temp-history-filter"
                  className="flex items-center justify-between gap-3 bg-eucalyptus-100 px-6 py-3 text-sm"
                >
                  <span>
                    History for <strong>{filterEqName}</strong> ({visibleLogs.length} readings)
                  </span>
                  <button
                    data-testid="temp-history-clear"
                    onClick={() => {
                      searchParams.delete("filter");
                      searchParams.delete("equipment");
                      setSearchParams(searchParams, { replace: true });
                    }}
                    className="text-ochre-600 hover:text-ochre-400 underline underline-offset-2 font-bold"
                  >
                    Show all
                  </button>
                </div>
              )}
              <div className="grid grid-cols-12 bg-bone-alt px-6 py-3 text-[11px] uppercase font-bold tracking-widest text-eucalyptus-700">
                <div className="col-span-3">Equipment</div>
                <div className="col-span-1 text-center">Temp</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Logged by</div>
                <div className="col-span-3">When</div>
              </div>
              {visibleLogs.length === 0 ? (
                <div className="p-10 text-center text-charcoal-soft text-sm">
                  {filterEqName ? "No readings yet for this equipment." : "No temperature logs yet."}
                </div>
              ) : (
                visibleLogs.map((l) => (
                  <div
                    key={l.id}
                    data-testid={`temp-row-${l.id}`}
                    className="grid grid-cols-12 px-6 py-4 border-t border-[color:var(--border)] items-center"
                  >
                    <div className="col-span-3 font-medium">{l.equipment_name}</div>
                    <div className="col-span-1 text-center font-mono font-bold">
                      {l.temperature_c}°
                    </div>
                    <div className="col-span-1 text-center">
                      {l.in_range ? (
                        <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100">
                          OK
                        </Badge>
                      ) : (
                        <Badge className="bg-ochre-600 text-white hover:bg-ochre-600">
                          BREACH
                        </Badge>
                      )}
                    </div>
                    <div className="col-span-2 capitalize text-sm">{l.log_type.replace("_", " ")}</div>
                    <div className="col-span-2 text-sm">{l.user_name}</div>
                    <div className="col-span-3 text-sm text-charcoal-soft font-mono">
                      {new Date(l.created_at).toLocaleString("en-AU")}
                    </div>
                  </div>
                ))
              )}
            </>
          );
        })()}
      </div>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={handleScan}
        title="Scan equipment sticker"
      />
    </div>
  );
}
